import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { DashboardFloatingActionButton } from '../../components/dashboard/DashboardFloatingActionButton'
import { TasksPanel } from '../../components/dashboard/TasksPanel'
import { parseTasksPanelTab, type TasksPanelTab } from '../../components/dashboard/tasksPanelTab'
import { HabitsPanel } from '../../components/Habits/HabitsPanel'
import { GoalsPanel } from '../../components/dashboard/GoalsPanel'
import LifeBalancePanel from '../../components/dashboard/LifeBalance/LifeBalancePanel'
import { NextUpPanel } from '../../components/dashboard/NextUpPanel'
import { ActionBar } from '../../components/ActionBar'
import { StreakSavedPanel } from '../../components/Streak/StreakSavedPanel'
import { Toast } from '../../components/Toast'
import { AlertVariants } from '../../components/Alert'
import { describeEventStart } from '../../components/Calendar/calendarUtils'

/** How long the "event added" confirmation stays up. */
const EVENT_ADDED_TOAST_MS = 5000

export const Route = createFileRoute('/_authenticated/dashboard')({
  component: DashboardPage,
  // `tab` tracks the Tasks panel's selected group ("Today" / "Upcoming" / "Unscheduled") in the
  // URL so creating a task and navigating back here restores the group the user was on.
  // `eventAdded` is set by the new event form after a save, to confirm when the event is; it is
  // dropped from the URL once the confirmation closes.
  validateSearch: (search): { tab?: TasksPanelTab; eventAdded?: string } => ({
    tab: parseTasksPanelTab(search.tab),
    eventAdded: describeEventStart(search.eventAdded) ? (search.eventAdded as string) : undefined,
  }),
})

function DashboardPage() {
  const navigate = useNavigate()
  const { tab, eventAdded } = Route.useSearch()
  const selectedTasksTab = tab ?? 'today'
  const eventAddedWhen = describeEventStart(eventAdded)

  const todaysDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })

  return (
    <div className="flex flex-col p-4">
      <ActionBar title="Dashboard" subtitle={todaysDate} />

      <NextUpPanel />

      <StreakSavedPanel />

      <div className="mt-4">
        <TasksPanel
          selectedTab={selectedTasksTab}
          onSelectedTabChange={(nextTab) =>
            navigate({
              to: '/dashboard',
              search: (prev) => ({ ...prev, tab: nextTab }),
              replace: true,
            })
          }
        />
      </div>

      <div className="mt-4">
        <HabitsPanel />
      </div>

      <div className="mt-4">
        <GoalsPanel />
      </div>

      <div className="mt-4">
        <LifeBalancePanel />
      </div>

      <DashboardFloatingActionButton currentTab={selectedTasksTab} />

      <Toast
        open={eventAddedWhen !== undefined}
        type={AlertVariants.success}
        title="Event added"
        durationMs={EVENT_ADDED_TOAST_MS}
        onClose={() => navigate({ to: '/dashboard', search: { tab }, replace: true })}
      >
        {eventAddedWhen}
      </Toast>
    </div>
  )
}
